import { useCallback, useEffect, useRef } from 'react'

interface UseAdFailSafeTimeoutsParams {
    isActive: boolean
    loading: boolean
    error: unknown
    onInitFailure: () => void
    getIsImaAvailable: () => boolean
    initTimeoutMs?: number
}

export const DEFAULT_AD_REQUEST_TIMEOUT_MS = 12000
export const DEFAULT_IMA_INIT_TIMEOUT_MS = 10000

export const useAdFailSafeTimeouts = ({
    isActive,
    loading,
    error,
    onInitFailure,
    getIsImaAvailable,
    initTimeoutMs = DEFAULT_IMA_INIT_TIMEOUT_MS,
}: UseAdFailSafeTimeoutsParams) => {
    const initTimeoutRef = useRef<number | null>(null)
    const requestTimeoutRef = useRef<number | null>(null)
    const initFailedRef = useRef(false)

    const clearInitTimeout = useCallback(() => {
        if (initTimeoutRef.current !== null) {
            window.clearTimeout(initTimeoutRef.current)
            initTimeoutRef.current = null
        }
    }, [])

    const clearRequestTimeout = useCallback(() => {
        if (requestTimeoutRef.current !== null) {
            window.clearTimeout(requestTimeoutRef.current)
            requestTimeoutRef.current = null
        }
    }, [])

    const startRequestTimeout = useCallback(
        (onTimeout: () => void, timeoutMs = DEFAULT_AD_REQUEST_TIMEOUT_MS) => {
            clearRequestTimeout()
            requestTimeoutRef.current = window.setTimeout(() => {
                onTimeout()
            }, timeoutMs)
        },
        [clearRequestTimeout],
    )

    const clearAllTimeouts = useCallback(() => {
        clearInitTimeout()
        clearRequestTimeout()
    }, [clearInitTimeout, clearRequestTimeout])

    useEffect(() => {
        if (!isActive) {
            initFailedRef.current = false
            clearInitTimeout()
            return
        }

        if (!loading) {
            clearInitTimeout()
            if ((error || !getIsImaAvailable()) && !initFailedRef.current) {
                initFailedRef.current = true
                onInitFailure()
            }
            return
        }

        clearInitTimeout()
        initTimeoutRef.current = window.setTimeout(() => {
            if (initFailedRef.current) return
            initFailedRef.current = true
            onInitFailure()
        }, initTimeoutMs)

        return () => {
            clearInitTimeout()
        }
    }, [
        clearInitTimeout,
        error,
        getIsImaAvailable,
        initTimeoutMs,
        isActive,
        loading,
        onInitFailure,
    ])

    useEffect(() => {
        return () => {
            clearAllTimeouts()
        }
    }, [clearAllTimeouts])

    return {
        startRequestTimeout,
        clearRequestTimeout,
        clearAllTimeouts,
    }
}
